import React from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    FormControlLabel,
    Grid,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import FormContainer from '../../../components/Form/FormContainer/FormContainer';
import DatacorLogoSpinner from '../../../components/DatacorLogoSpinner/DatacorLogoSpinner';
import FixedAssetsFormHeader from '../../../components/Form/FormContainer/FormHeader';
import FormPartial from '../../../components/Form/FormContainer/FormPartial';
import TextInput from '../../../components/Form/FormInputs/TextInput/TextInput';
import useFixedAssetDepreciationMethodTypeForm from '../hooks/useFixedAssetDepreciationMethodTypeForm';
import ToastContainer from '../../../components/Toast/ToastContainer';
import NumericInput from '../../../components/Form/FormInputs/NumericInput/NumericInput';

interface ParamTypes {
    typeId: string;
}

const FixedAssetDepreciationMethodTypeForm = () => {
    const { typeId } = useParams<ParamTypes>();
    const {
        fields,
        handleFieldChange,
        formMethods,
        toastProps,
        onCreate,
        onUpdate,
        onDelete,
        closeForm,
        isLoadingType,
        onPredefinedScheduleChanged,
        onUsageBasedChanged,
    } = useFixedAssetDepreciationMethodTypeForm(typeId);

    return (
        <Card>
            <CardHeader title="Fixed Asset Depreciation Method Type" />

            {isLoadingType ? (
                <DatacorLogoSpinner />
            ) : (
                <CardContent>
                    <ToastContainer {...toastProps} show={!!toastProps?.show} />

                    <FormContainer
                        headerTitle={'Primary Information'}
                        onSubmit={() => formMethods.setIsFormSubmitted(true)}
                        headerComponent={
                            <FixedAssetsFormHeader
                                showOnDelete={!!typeId}
                                showOnSave={true}
                                onSave={() => {
                                    if (!typeId) {
                                        onCreate();
                                    } else {
                                        onUpdate();
                                    }
                                }}
                                onDelete={() => {
                                    onDelete();
                                }}
                                onCancel={closeForm}
                                isEdit={!!typeId}
                            />
                        }>
                        <FormPartial>
                            <Grid container flexDirection="row" spacing={2}>
                                <Grid item xs={6}>
                                    <TextInput
                                        fullWidth
                                        label="Depreciation Method Type Code"
                                        id="code"
                                        value={fields.code}
                                        onChange={handleFieldChange}
                                        formMethods={formMethods}
                                        required={true}
                                        maxLength={30}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <NumericInput
                                        fullWidth
                                        label="Sort Order"
                                        id="sortOrder"
                                        value={fields.sortOrder}
                                        onChange={handleFieldChange}
                                        formMethods={formMethods}
                                        required={true}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextInput
                                        fullWidth
                                        label="Description"
                                        id="description"
                                        value={fields.description}
                                        onChange={handleFieldChange}
                                        formMethods={formMethods}
                                        maxLength={300}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <FormControlLabel
                                        label="Journal Posting"
                                        control={
                                            <Checkbox
                                                id="postsJournalEntries"
                                                checked={
                                                    fields.postsJournalEntries
                                                }
                                                onChange={handleFieldChange}
                                            />
                                        }
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <FormControlLabel
                                        label="Predefined Schedule"
                                        control={
                                            <Checkbox
                                                id="predefinedSchedule"
                                                checked={
                                                    fields.predefinedSchedule
                                                }
                                                onChange={
                                                    onPredefinedScheduleChanged
                                                }
                                            />
                                        }
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <FormControlLabel
                                        label="Usage Based"
                                        control={
                                            <Checkbox
                                                id="usageBased"
                                                checked={fields.usageBased}
                                                onChange={onUsageBasedChanged}
                                            />
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </FormPartial>
                    </FormContainer>
                </CardContent>
            )}
        </Card>
    );
};

export default FixedAssetDepreciationMethodTypeForm;
